/**
 * Time and timestamp utility functions
 * Handles conversion between various timestamp formats (protobuf, Unix, ISO strings)
 */

import { Timestamp } from "../types/api";

/**
 * Convert protobuf timestamp to JavaScript timestamp (milliseconds)
 * Handles both protobuf format { seconds, nanos } and regular number/string timestamps
 * 
 * @param ts - Timestamp in protobuf format { seconds, nanos }, number, or string
 * @param fallback - Fallback timestamp if conversion fails (default: Date.now())
 * @returns JavaScript timestamp in milliseconds
 * 
 * @example
 * normalizeProtobufTimestamp({ seconds: 1766832240, nanos: 366255190 }) // Returns: 1766832243366
 * normalizeProtobufTimestamp(1766832243366) // Returns: 1766832243366
 * normalizeProtobufTimestamp("1766832243") // Returns: 1766832243000 (converts seconds to ms)
 */
export function normalizeProtobufTimestamp(
  ts: any,
  fallback: number = Date.now()
): number {
  if (!ts) return fallback;
  
  // Handle protobuf timestamp format: { seconds: number, nanos?: number }
  if (typeof ts === "object" && ts !== null && typeof ts.seconds === "number") {
    const seconds = ts.seconds;
    const nanos = ts.nanos || 0;
    // Convert to milliseconds: seconds * 1000 + nanos / 1000000
    return seconds * 1000 + Math.floor(nanos / 1000000);
  }
  
  // Handle regular number or string timestamp
  const num = typeof ts === "string" ? Number(ts) : ts;
  if (typeof num === "number" && !isNaN(num) && num > 0) {
    // If timestamp is in seconds (less than year 2000 in milliseconds), convert to milliseconds
    if (num < 946684800000) return num * 1000;
    return num;
  }
  
  return fallback;
}

/**
 * Normalize timestamp (string or number) to milliseconds
 * Handles both Unix timestamps in seconds and milliseconds
 * 
 * @param ts - Timestamp as string or number
 * @returns JavaScript timestamp in milliseconds
 * 
 * @example
 * normalizeTimestamp(1766832243) // Returns: 1766832243000 (converts seconds to ms)
 * normalizeTimestamp(1766832243000) // Returns: 1766832243000 (already in ms)
 * normalizeTimestamp("1766832243") // Returns: 1766832243000
 */
export function normalizeTimestamp(
  ts: string | number | undefined | null
): number {
  if (!ts) return Date.now();
  const num = typeof ts === "string" ? Number(ts) : ts;
  if (isNaN(num) || num <= 0) return Date.now();
  // If timestamp is in seconds (less than year 2000 in milliseconds), convert to milliseconds
  if (num < 946684800000) return num * 1000;
  return num;
}

/**
 * Convert protobuf Timestamp to ISO string
 * Handles both string timestamps (backward compatibility) and protobuf Timestamp objects
 * 
 * @param timestamp - Either an ISO string or protobuf Timestamp object { seconds, nanos }
 * @returns ISO timestamp string
 * 
 * @example
 * timestampToISOString({ seconds: 1766832240, nanos: 366255190 })
 * // Returns: "2025-12-17T13:32:06.366255190Z"
 * timestampToISOString("2025-12-17T13:32:06Z") // Returns: "2025-12-17T13:32:06Z"
 */
export function timestampToISOString(
  timestamp: string | Timestamp | undefined
): string {
  if (!timestamp) return "";
  
  // If already a string, return as-is
  if (typeof timestamp === "string") {
    return timestamp;
  }
  
  // If it's a protobuf Timestamp object, convert to ISO string
  if (typeof timestamp === "object" && "seconds" in timestamp && "nanos" in timestamp) {
    const seconds = timestamp.seconds;
    const nanos = timestamp.nanos || 0;
    
    // Convert to milliseconds for Date constructor
    const milliseconds = seconds * 1000 + Math.floor(nanos / 1000000);
    const date = new Date(milliseconds);
    
    // Format as ISO string with nanoseconds precision
    const isoString = date.toISOString();
    
    // If nanos exist, replace the milliseconds part with nanoseconds
    if (nanos > 0) {
      const nanosStr = nanos.toString().padStart(9, "0");
      return isoString.replace(/\.\d{3}Z$/, `.${nanosStr}Z`);
    }
    
    return isoString;
  }
  
  return "";
}

/**
 * Convert Date to RFC3339Nano format string
 * RFC3339Nano format: "2006-01-02T15:04:05.999999999Z" (matches Go's time.RFC3339Nano)
 * 
 * @param date - Date object (defaults to current time if not provided)
 * @returns RFC3339Nano formatted timestamp string
 * 
 * @example
 * toRFC3339Nano() // Returns: "2025-12-17T13:32:06.123456789Z"
 * toRFC3339Nano(new Date(1766832243366)) // Returns: "2025-12-17T13:32:06.366000000Z"
 */
export function toRFC3339Nano(date?: Date): string {
  const d = date || new Date();
  const isoString = d.toISOString();
  
  // Extract milliseconds from ISO string (format: YYYY-MM-DDTHH:mm:ss.sssZ)
  // Convert milliseconds to nanoseconds (9 digits) and replace in ISO string
  const match = isoString.match(/\.(\d{3})Z$/);
  if (match) {
    const milliseconds = parseInt(match[1]);
    // Convert milliseconds to nanoseconds (pad to 9 digits)
    const nanoseconds = (milliseconds * 1000000).toString().padStart(9, "0");
    return isoString.replace(/\.\d{3}Z$/, `.${nanoseconds}Z`);
  }
  
  // If no milliseconds found, add .000000000
  return isoString.replace(/Z$/, ".000000000Z");
}

